// DevWorkspaceLayout.js — Split workspace : tree | preview/editor | terminal

import React, { useState, useCallback, useEffect, useRef } from 'react';
import DevWorkspaceToolbar from './DevWorkspaceToolbar';
import DevFileTree from './DevFileTree';
import DevCodeEditor from './DevCodeEditor';
import DevFilePreview from './DevFilePreview';
import DevAppPreview from './DevAppPreview';
import DevSaveConfirmModal from './DevSaveConfirmModal';
import DevPreviewConfirmModal from './DevPreviewConfirmModal';
import DevTerminal from './DevTerminal';
import DevGitPanel from './DevGitPanel';
import {
    getWorkspace, initWorkspace, getWorkspaceTree, readWorkspaceFile, writeWorkspaceFile,
    DevCockpitApiError,
} from '../../services/devCockpitApi';
import './devCockpit.css';

export default function DevWorkspaceLayout() {
    const [workspace, setWorkspace] = useState(null);
    const [tree, setTree] = useState([]);
    const [treeTruncated, setTreeTruncated] = useState(false);
    const [treeTotal, setTreeTotal] = useState(0);
    const [treeLoading, setTreeLoading] = useState(true);
    const [selectedPath, setSelectedPath] = useState(null);
    const [language, setLanguage] = useState('plaintext');
    const [content, setContent] = useState('');
    const [savedContent, setSavedContent] = useState('');
    const [fileLoading, setFileLoading] = useState(false);
    const [viewMode, setViewMode] = useState('file');
    const [editorDirty, setEditorDirty] = useState(false);
    const [initializing, setInitializing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [confirmSave, setConfirmSave] = useState(false);
    const [confirmApp, setConfirmApp] = useState(false);
    const [error, setError] = useState(null);
    const [terminalError, setTerminalError] = useState(null);

    const getEditorValueRef = useRef(null);

    const dirty = viewMode === 'edit' ? editorDirty : false;

    const loadWorkspace = useCallback(async () => {
        try {
            const { workspace: ws } = await getWorkspace();
            setWorkspace(ws);
            return ws;
        } catch (e) {
            setError(e instanceof DevCockpitApiError ? e.message : 'Erreur workspace');
            return null;
        }
    }, []);

    const loadTree = useCallback(async () => {
        setTreeLoading(true);
        try {
            const data = await getWorkspaceTree('', true);
            setTree(data.entries || []);
            setTreeTruncated(!!data.truncated);
            setTreeTotal(data.total || (data.entries || []).length);
            setError(null);
        } catch (e) {
            if (e.status !== 422) {
                setError(e instanceof DevCockpitApiError ? e.message : 'Erreur arborescence');
            }
            setTree([]);
        } finally {
            setTreeLoading(false);
        }
    }, []);

    useEffect(() => {
        (async () => {
            const ws = await loadWorkspace();
            if (ws?.status === 'ready') await loadTree();
            else setTreeLoading(false);
        })();
    }, [loadWorkspace, loadTree]);

    const handleInit = async () => {
        setInitializing(true);
        setError(null);
        try {
            const { workspace: ws } = await initWorkspace();
            setWorkspace(ws);
            if (ws.status === 'ready') await loadTree();
        } catch (e) {
            setError(e instanceof DevCockpitApiError ? e.message : 'Échec init clone');
        } finally {
            setInitializing(false);
        }
    };

    const handleSelectFile = async (filePath) => {
        if (dirty && !window.confirm('Modifications non enregistrées — continuer ?')) return;
        setFileLoading(true);
        setViewMode('file');
        setEditorDirty(false);
        setError(null);
        try {
            const { file } = await readWorkspaceFile(filePath);
            setSelectedPath(filePath);
            setContent(file.content);
            setSavedContent(file.content);
            setLanguage(file.language || 'plaintext');
        } catch (e) {
            setError(e instanceof DevCockpitApiError ? e.message : 'Erreur lecture fichier');
        } finally {
            setFileLoading(false);
        }
    };

    const handleSaveRequest = () => {
        if (!dirty || !selectedPath) return;
        setConfirmSave(true);
    };

    const handleSaveConfirm = async () => {
        setSaving(true);
        try {
            const toSave = getEditorValueRef.current?.() ?? content;
            await writeWorkspaceFile(selectedPath, toSave);
            setContent(toSave);
            setSavedContent(toSave);
            setEditorDirty(false);
            setConfirmSave(false);
            setError(null);
        } catch (e) {
            setError(e instanceof DevCockpitApiError ? e.message : 'Erreur enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const handleWorkspaceRefresh = useCallback(async () => {
        await loadTree();
        if (selectedPath) {
            try {
                const { file } = await readWorkspaceFile(selectedPath);
                setContent(file.content);
                setSavedContent(file.content);
                setLanguage(file.language || 'plaintext');
                setEditorDirty(false);
            } catch { /* ignore */ }
        }
    }, [loadTree, selectedPath]);

    const flushEditorContent = useCallback(() => {
        const latest = getEditorValueRef.current?.();
        if (latest !== undefined) setContent(latest);
    }, []);

    const handleSetViewMode = useCallback((mode) => {
        if (mode === viewMode) return;
        if (dirty && !window.confirm('Modifications non enregistrées — continuer ?')) return;
        if (viewMode === 'edit') flushEditorContent();
        setViewMode(mode);
    }, [viewMode, dirty, flushEditorContent]);

    const handleRequestApp = useCallback(() => {
        if (viewMode === 'app') return;
        if (dirty && !window.confirm('Modifications non enregistrées — continuer ?')) return;
        if (viewMode === 'edit') flushEditorContent();
        setConfirmApp(true);
    }, [viewMode, dirty, flushEditorContent]);

    const handleConfirmApp = useCallback(() => {
        setConfirmApp(false);
        setViewMode('app');
    }, []);

    const handleDirtyChange = useCallback((isDirty) => {
        setEditorDirty(isDirty);
    }, []);

    const handleRegisterGetValue = useCallback((fn) => {
        getEditorValueRef.current = fn;
    }, []);

    const workspaceReady = workspace?.status === 'ready';

    return (
        <div className="dev-workspace">
            <DevWorkspaceToolbar
                workspace={workspace}
                selectedPath={selectedPath}
                language={language}
                dirty={dirty}
                viewMode={viewMode}
                onInit={handleInit}
                onSave={handleSaveRequest}
                onRefresh={loadTree}
                onSetViewMode={handleSetViewMode}
                onRequestApp={handleRequestApp}
                initializing={initializing}
                saving={saving}
                fileLoading={fileLoading}
            />

            {error && <div className="dev-workspace__error">{error}</div>}

            <div className="dev-workspace__main">
                <DevFileTree
                    entries={tree}
                    selectedPath={selectedPath}
                    onSelectFile={handleSelectFile}
                    loading={treeLoading}
                    truncated={treeTruncated}
                    total={treeTotal}
                />
                <div className="dev-workspace__content-col">
                    {viewMode === 'edit' ? (
                        <DevCodeEditor
                            path={selectedPath}
                            language={language}
                            initialContent={content}
                            savedContent={savedContent}
                            onDirtyChange={handleDirtyChange}
                            onRegisterGetValue={handleRegisterGetValue}
                        />
                    ) : viewMode === 'app' ? (
                        <DevAppPreview workspaceReady={workspaceReady} active />
                    ) : (
                        <DevFilePreview
                            path={selectedPath}
                            language={language}
                            content={content}
                            loading={fileLoading}
                        />
                    )}
                    {viewMode === 'file' && (
                        <DevGitPanel
                            workspaceReady={workspaceReady}
                            selectedPath={selectedPath}
                            onRefreshWorkspace={handleWorkspaceRefresh}
                        />
                    )}
                    {viewMode !== 'edit' && (
                        <DevTerminal
                            workspaceReady={workspaceReady}
                            onError={setTerminalError}
                        />
                    )}
                    {viewMode === 'edit' && (
                        <div className="dev-terminal__paused">
                            Terminal suspendu en mode édition — reprend en aperçu ou App
                        </div>
                    )}
                    {terminalError && (
                        <div className="dev-terminal__error">{terminalError}</div>
                    )}
                </div>
            </div>

            {confirmApp && (
                <DevPreviewConfirmModal
                    onConfirm={handleConfirmApp}
                    onCancel={() => setConfirmApp(false)}
                />
            )}

            {confirmSave && (
                <DevSaveConfirmModal
                    path={selectedPath}
                    onConfirm={handleSaveConfirm}
                    onCancel={() => setConfirmSave(false)}
                    saving={saving}
                />
            )}
        </div>
    );
}
